import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface NotificationItemProps {
  notification: any;
  onMarkAsRead: (id: string) => void;
  onRemove: (id: string) => void;
  loading: boolean;
}

const NotificationItem = ({ notification, onMarkAsRead, onRemove, loading }: NotificationItemProps) => {
  const { t } = useTranslation();

  const isCourseAssignmentRequest =
    notification.type === "course_assignment_request" ||
    notification.title?.toLowerCase().includes("course assignment request") ||
    notification.metadata?.notificationType === "course_assignment_request" ||
    notification.message?.toLowerCase().includes("course assignment request");

  const isLoginNotification =
    notification.type === "login" ||
    notification.title?.toLowerCase().includes("login") ||
    notification.title?.toLowerCase().includes("successful") ||
    notification.message?.toLowerCase().includes("login") ||
    notification.message?.toLowerCase().includes("successful");

  const isSecurityNotification =
    notification.type === "security" ||
    notification.title?.toLowerCase().includes("security") ||
    notification.title?.toLowerCase().includes("password") ||
    notification.title?.toLowerCase().includes("failed") ||
    notification.message?.toLowerCase().includes("security") ||
    notification.message?.toLowerCase().includes("password") ||
    notification.message?.toLowerCase().includes("failed");

  const isPolicyNotification =
    notification.type === "policy" ||
    notification.title?.toLowerCase().includes("policy") ||
    notification.title?.toLowerCase().includes("update") ||
    notification.message?.toLowerCase().includes("policy") ||
    notification.message?.toLowerCase().includes("update");

  const getNotificationIcon = () => {
    if (isCourseAssignmentRequest) {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
          <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
      );
    }
    if (isLoginNotification) {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
          <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }
    if (isSecurityNotification) {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
          <svg className="h-4 w-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
      );
    }
    if (isPolicyNotification) {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
          <svg className="h-4 w-4 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
        <svg className="h-4 w-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-6H4v6z" />
        </svg>
      </div>
    );
  };

  const getNotificationColor = () => {
    if (isCourseAssignmentRequest) return "border-l-blue-500";
    if (isLoginNotification) return "border-l-green-500";
    if (isSecurityNotification) return "border-l-red-500";
    if (isPolicyNotification) return "border-l-yellow-500";
    return "border-l-gray-500";
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`relative border-l-4 ${getNotificationColor()} bg-white px-4 py-3 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 ${
        !notification.isRead ? "bg-blue-50 dark:bg-blue-900/10" : ""
      }`}
    >
      <div className="flex items-start space-x-3">
        {getNotificationIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {notification.title || t("admin.notifications.defaultTitle")}
            </p>
            <div className="flex items-center space-x-2">
              {!notification.isRead && (
                <div className="h-2 w-2 rounded-full bg-blue-600" />
              )}
              <button
                onClick={() => onRemove(notification._id)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={loading}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {notification.message || t("admin.notifications.defaultMessage")}
          </p>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(notification.createdAt).toLocaleString()}
            </p>
            {!notification.isRead && (
              <button
                onClick={() => onMarkAsRead(notification._id)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {t("admin.notifications.markAsRead")}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NotificationItem;

